import { AxiosResponse } from 'axios'
import { infiniteQueryOptions, keepPreviousData } from '@tanstack/react-query'
import { CommentDetail, CommentsSurface } from '../generated/api/comment-row'
import { PostRole } from '../generated/api/post-row'
import { createUrl, definedParams, isRefusedRequest } from './helpers'
import axiosAuth from './config'

export interface DiscussionPage {
  cursor?: string
  target?: string
}

export const getCommentDiscussion = (
  postId: string,
  page: DiscussionPage,
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: CommentsSurface }>> => {
  return axiosAuth.get<{ data: CommentsSurface }>(
    createUrl(
      `/comments/discussion/${postId}`,
      definedParams({ cursor: page.cursor, target: page.target })
    ),
    { signal }
  )
}

export const getCommentRow = (
  id: string,
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: CommentDetail }>> => {
  return axiosAuth.get<{ data: CommentDetail }>(createUrl(`/comments/${id}`), {
    signal,
  })
}

export const toggleLike = (
  id: string,
  like: boolean
): Promise<AxiosResponse<unknown>> => {
  return axiosAuth.post(createUrl(`/comments/toggle-like/${id}`), {
    value: like,
  })
}

export const createComment = (body: {
  postId: string
  message: string
  role: PostRole
}): Promise<AxiosResponse<{ data: { _id: string } | null }>> => {
  return axiosAuth.post<{ data: { _id: string } | null }>(
    createUrl('/comments/comment'),
    body
  )
}

export const deleteComment = (id: string): Promise<AxiosResponse<unknown>> => {
  return axiosAuth.delete(createUrl(`/comments/comment/${id}`))
}

export const patchComment = (variables: {
  id: string
  message: string
}): Promise<AxiosResponse<unknown>> => {
  return axiosAuth.patch(createUrl(`/comments/comment/${variables.id}`), {
    message: variables.message,
  })
}

export const discussionQueryKey = (postId: string) =>
  [{ scope: 'comments', entity: 'discussion', postId }] as const

export const commentQueries = {
  discussion: (postId?: string, target?: string) =>
    infiniteQueryOptions({
      queryKey: [
        { scope: 'comments', entity: 'discussion', postId, target },
      ] as const,
      placeholderData: keepPreviousData,
      enabled: !!postId,
      initialPageParam: { target } as DiscussionPage,
      queryFn: async ({ pageParam, queryKey, signal }) =>
        (
          await getCommentDiscussion(
            queryKey[0].postId as string,
            pageParam,
            signal
          )
        ).data.data,
      getNextPageParam: (lastPage): DiscussionPage | undefined =>
        lastPage.laterCursor == null
          ? undefined
          : { cursor: lastPage.laterCursor },
      getPreviousPageParam: (firstPage): DiscussionPage | undefined =>
        firstPage.earlierCursor == null
          ? undefined
          : { cursor: firstPage.earlierCursor },
      retry: (failureCount, error) =>
        !isRefusedRequest(error) && failureCount < 3,
    }),
}
